import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError, mediaUrl } from "../lib/api";
import { useToast } from "../lib/toast";
import { ARTIST_LANGUAGE_OPTIONS, getArtistLanguageSelection, getProfileLanguages } from "../lib/artistLanguages";
import ImageCropDialog from "./ImageCropDialog";
import { MAX_PORTFOLIO_FILE_BYTES, MAX_PORTFOLIO_FILE_LABEL, prepareImageForUpload, uploadInChunks, uploadPortfolioFile } from "../lib/mediaUpload";
import CitySearchField from "./CitySearchField";
import { loadIndianCityCatalog } from "../lib/indianCities";
import ArtistCategoryMultiSelect from "./ArtistCategoryMultiSelect";

/**
 * 5-step Artist Onboarding Wizard.
 * Renders as a full-screen overlay on top of /artist when status.completed === false.
 * Auto-resumes from the next incomplete step.
 */
export default function OnboardingWizard({ user, onComplete, onClose }) {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const [basics, setBasics] = useState({ stage_name: "", categories: [], city: "", phone: "" });
  const [categoryList, setCategoryList] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [popularCityOptions, setPopularCityOptions] = useState([]);
  const [cityAliases, setCityAliases] = useState({});
  const [cityOptionsError, setCityOptionsError] = useState(false);
  const [branding, setBranding] = useState({ bio: "", languages: [], languages_other: "", experience_years: 0 });
  const [pkg, setPkg] = useState({ name: "Standard Set", price: 50000, duration: 2, features: [] });
  const [availDate, setAvailDate] = useState("");
  const [profileImg, setProfileImg] = useState(null);
  const [coverImg, setCoverImg] = useState(null);
  const [cropRequest, setCropRequest] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [existingGallery, setExistingGallery] = useState([]);
  const [deletingMediaId, setDeletingMediaId] = useState("");
  const [techRider, setTechRider] = useState(null);
  const [techRiderFile, setTechRiderFile] = useState(null);
  const profileRef = useRef();
  const coverRef = useRef();
  const galleryRef = useRef();

  const chooseBrandingImage = (event, type) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) setCropRequest({ file, type });
  };

  const chooseGalleryFiles = (event) => {
    setGalleryFiles(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const finishBrandingCrop = (file) => {
    if (cropRequest?.type === "profile") setProfileImg(file);
    if (cropRequest?.type === "cover") setCoverImg(file);
    if (cropRequest?.type === "gallery") {
      setGalleryFiles((current) => current.map((item, index) => (
        index === cropRequest.index ? file : item
      )));
    }
    setCropRequest(null);
  };

  useEffect(() => { reload(); }, []);

  useEffect(() => {
    api.get("/catalog/categories")
      .then((response) => setCategoryList(response.data || []))
      .catch((error) => toast(formatApiError(error), "error"));
  }, [toast]);

  useEffect(() => {
    loadIndianCityCatalog("artist")
      .then(({ popular, cities, aliases }) => {
        setPopularCityOptions(popular.map((city) => city.name));
        setCityOptions(cities.map((city) => city.name));
        setCityAliases(aliases);
      })
      .catch(() => setCityOptionsError(true));
  }, []);

  useEffect(() => {
    if (step !== 3) return;
    api.get("/media", { params: { type: "gallery" } })
      .then((response) => setExistingGallery(response.data || []))
      .catch((error) => toast(formatApiError(error), "error"));
    api.get("/artist/tech-rider/mine")
      .then((response) => setTechRider(response.data?.file || null))
      .catch((error) => toast(formatApiError(error), "error"));
  }, [step, toast]);

  const reload = async () => {
    const r = await api.get("/onboarding/me");
    setStatus(r.data);
    if (r.data.required && !r.data.completed) setStep(r.data.current_step || r.data.next_step || 1);
    // Pre-fill basics
    const me = await api.get("/auth/me");
    const p = me.data.artist_profile || {};
    const categories = Array.isArray(p.categories) && p.categories.length ? p.categories : (p.category ? [p.category] : []);
    setBasics({
      stage_name: p.stage_name || `${me.data.first_name} ${me.data.last_name || ""}`.trim(),
      categories,
      city: p.city || "",
      phone: me.data.phone || "",
    });
    const languageSelection = getArtistLanguageSelection(p.languages, p.answers || {});
    setBranding({
      bio: p.bio || "",
      languages: languageSelection.selected,
      languages_other: languageSelection.otherText,
      experience_years: p.experience_years || 0,
    });
  };

  const fileToDataUrl = (f) => new Promise((r) => { const x = new FileReader(); x.onload = () => r(x.result); x.readAsDataURL(f); });

  const saveStep1 = async () => {
    const selectedCategories = [...new Set(basics.categories.map((category) => category.trim()).filter(Boolean))];
    if (!basics.stage_name || !selectedCategories.length || !basics.city) { toast("Please fill all fields and select at least one category", "error"); return; }
    setBusy(true);
    try {
      await api.put("/users/me", {
        phone: basics.phone, stage_name: basics.stage_name,
        category: selectedCategories[0], categories: selectedCategories, city: basics.city, onboarding_step: 2,
      });
      toast("Saved");
      setStep(2);
      reload();
    } catch (e) { toast(formatApiError(e), "error"); }
    setBusy(false);
  };

  const saveStep2 = async () => {
    setBusy(true);
    try {
      if (profileImg) {
        const du = await fileToDataUrl(await prepareImageForUpload(profileImg));
        await api.post("/media/upload", { type: "profile", data_url: du, title: "Profile" });
      }
      if (coverImg) {
        const du = await fileToDataUrl(await prepareImageForUpload(coverImg));
        await api.post("/media/upload", { type: "cover", data_url: du, title: "Cover" });
      }
      const selectedLanguages = branding.languages || [];
      const otherLanguages = selectedLanguages.includes("Other") ? branding.languages_other.trim() : "";
      const profileLanguages = getProfileLanguages(selectedLanguages, otherLanguages);
      await api.put("/users/me", {
        bio: branding.bio,
        languages: profileLanguages,
        experience_years: Number(branding.experience_years) || 0,
        onboarding_step: 3,
      });
      await api.post("/questionnaire/answers", {
        answers: { languages: selectedLanguages, languages_other: otherLanguages },
      });
      toast("Branding saved");
      setStep(3);
      reload();
    } catch (e) { toast(formatApiError(e), "error"); }
    setBusy(false);
  };

  const saveStep3 = async () => {
    if (galleryFiles.length === 0 && !(status?.checks?.step3_media)) {
      toast("Upload at least one media item", "error"); return;
    }
    if (techRiderFile && (techRiderFile.type !== "application/pdf" || techRiderFile.size > 10 * 1024 * 1024)) {
      toast("Tech Rider must be a PDF no larger than 10 MB", "error");
      return;
    }
    setBusy(true);
    try {
      for (const f of galleryFiles) {
        const isVideo = f.type.startsWith("video/");
        // Photos up to 40 MB (shrunk before upload); videos up to 100 MB, sent in chunks.
        const limit = isVideo ? 100 * 1024 * 1024 : MAX_PORTFOLIO_FILE_BYTES;
        if (f.size > limit) { toast(`${f.name} too large (max ${isVideo ? "100 MB" : MAX_PORTFOLIO_FILE_LABEL})`, "error"); continue; }
        if (isVideo) await uploadInChunks(f, { type: "video" });
        else await uploadPortfolioFile(f, { type: "gallery" });
      }
      if (techRiderFile) {
        const formData = new FormData();
        formData.append("file", techRiderFile);
        const response = await api.post("/artist/tech-rider/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setTechRider(response.data?.file || null);
        setTechRiderFile(null);
      }
      await api.put("/users/me", { onboarding_step: 4 });
      toast("Portfolio uploaded");
      setStep(4);
      reload();
    } catch (e) { toast(formatApiError(e), "error"); }
    setBusy(false);
  };

  const removeExistingGalleryItem = async (media) => {
    if (!window.confirm(`Remove "${media.title || "this image"}" from your portfolio?`)) return;
    setDeletingMediaId(media.id);
    try {
      await api.delete(`/media/${media.id}`);
      setExistingGallery((items) => items.filter((item) => item.id !== media.id));
      const response = await api.get("/onboarding/me");
      setStatus(response.data);
      toast("Image removed from your portfolio.");
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setDeletingMediaId("");
    }
  };

  const saveStep4 = async () => {
    if (!pkg.name || !pkg.price) { toast("Fill package details", "error"); return; }
    const durationHours = Number(pkg.duration);
    if (!Number.isFinite(durationHours) || durationHours <= 0) {
      toast("Enter a valid package duration in hours", "error");
      return;
    }
    setBusy(true);
    try {
      // Only create if no packages yet
      const existing = await api.get("/packages/mine");
      if (existing.data.length === 0) {
        await api.post("/packages", {
          name: pkg.name, price: Number(pkg.price), duration: `${durationHours} hours`,
          features: pkg.features.map((feature) => feature.trim()).filter(Boolean), is_popular: true,
        });
      }
      await api.put("/users/me", { onboarding_step: 5 });
      toast("Package saved");
      setStep(5);
      reload();
    } catch (e) { toast(formatApiError(e), "error"); }
    setBusy(false);
  };

  const saveStep5 = async (saveAvailability = true) => {
    setBusy(true);
    try {
      if (saveAvailability && availDate) await api.post("/availability", { date: availDate, status: "available" });
      // The artist has seen the calendar step, so it counts as reviewed either way.
      await api.post("/availability/reviewed");
      const result = await api.post("/onboarding/complete");
      toast(result.data?.message || "Profile setup complete. Complete KYC review before going live.");
      onComplete?.();
    } catch (e) { toast(formatApiError(e), "error"); }
    setBusy(false);
  };

  const skip = async () => {
    await api.post("/onboarding/complete");
    onComplete?.();
  };

  const selectAvailabilityDate = (daysFromToday) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromToday);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    setAvailDate(`${year}-${month}-${day}`);
  };

  const toggleLanguage = (language) => {
    setBranding((current) => ({
      ...current,
      languages: current.languages.includes(language)
        ? current.languages.filter((selected) => selected !== language)
        : [...current.languages, language],
    }));
  };

  // Iter 91 — Persistent dismiss without marking onboarding as complete.
  // Sets seen_welcome_at so it doesn't auto-open again; artist can still
  // return to it from the KYC/Profile tab.
  const dismiss = () => {
    onClose?.();
  };

  if (!status || !status.required || status.completed) return null;

  return (
    <div className="modal-bg" style={{ placeItems: "start center", overflowX: "hidden", overflowY: "auto", padding: "5vh 16px 16px" }} data-testid="onboarding-wizard">
      <div className="modal-card" style={{ maxWidth: 720, width: "100%", minWidth: 0, maxHeight: "90vh", overflowY: "auto", padding: 0, margin: "0 auto" }}>
        <div style={{ padding: "var(--space-6) 28px", borderBottom: "1px solid var(--glass-border)" }}>
          <div className="flex justify-between items-center mb-12">
            <div className="font-serif fs-20 fw-700">Welcome to <span className="text-gold">BookTalent</span></div>
            <div className="flex gap-8">
              <button className="btn btn-ghost btn-xs" onClick={skip} data-testid="wiz-skip">Skip for now</button>
              <button className="btn btn-ghost btn-xs" onClick={dismiss} data-testid="wiz-dismiss" title="Close and don't show again on next login">×</button>
            </div>
          </div>
          <div className="text-muted fs-13">Get booking-ready in 5 quick steps · Step {step} of 5</div>
          <div className="steps mt-12" style={{ marginBottom: 0 }}>
            {[1, 2, 3, 4, 5].map((n, i) => (
              <React.Fragment key={n}>
                <div className="step-node">
                  <div className={`step-circle ${step === n ? "active" : step > n ? "done" : ""}`} style={{ width: 30, height: 30, fontSize: 12 }}>
                    {step > n ? "✓" : n}
                  </div>
                </div>
                {i < 4 && <div className={`step-line ${step > n ? "done" : ""}`} style={{ width: 40 }} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div style={{ padding: "var(--space-6)", minWidth: 0 }}>
          {step === 1 && (
            <div data-testid="wiz-step-1">
              <div className="font-serif fs-20 fw-700 mb-8">Basic Information</div>
              <div className="text-muted fs-13 mb-20">Let's start with the basics.</div>
              <div className="field">
                <div className="field-label">Stage Name *</div>
                <input className="field-input" value={basics.stage_name} onChange={(e) => setBasics({ ...basics, stage_name: e.target.value })} data-testid="wiz-stage-name" />
              </div>
              <div className="field-row">
                <div className="field">
                  <div className="field-label">Artist Categories * <span className="text-muted">(select all that apply)</span></div>
                  <ArtistCategoryMultiSelect
                    categories={basics.categories}
                    options={categoryList.map((category) => category.name)}
                    onChange={(categories) => setBasics((current) => ({ ...current, categories }))}
                    allowCustom
                    testId="wiz-categories"
                  />
                </div>
                <div className="field">
                  <div className="field-label">Primary City *</div>
                  <CitySearchField
                    className="field-input"
                    city={basics.city}
                    cities={cityOptions}
                    popularCities={popularCityOptions}
                    cityAliases={cityAliases}
                    allowCustom
                    onCityChange={(city) => setBasics((current) => ({ ...current, city }))}
                    listId="onboarding-primary-city-options"
                    placeholder="Search or enter any Indian city"
                    data-testid="wiz-city"
                  />
                  {cityOptionsError && (
                    <div className="text-muted fs-11 mt-4">City suggestions are temporarily unavailable. You can still enter your city.</div>
                  )}
                </div>
              </div>
              <div className="field">
                <div className="field-label">Mobile</div>
                <input className="field-input" value={basics.phone} onChange={(e) => setBasics({ ...basics, phone: e.target.value })} data-testid="wiz-phone" />
              </div>
              <button className="btn btn-gold btn-block" onClick={saveStep1} disabled={busy} data-testid="wiz-next-1">
                {busy ? "Saving…" : "Continue →"}
              </button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="wiz-step-2">
              <div className="font-serif fs-20 fw-700 mb-8">Profile Branding</div>
              <div className="text-muted fs-13 mb-20">Make your profile stand out.</div>
              <div className="grid grid-2 mb-16">
                <div className="upload-zone" data-testid="wiz-profile-zone" onClick={() => profileRef.current?.click()} style={{ padding: "var(--space-4)" }}>
                  <input ref={profileRef} type="file" accept="image/*" onChange={(e) => chooseBrandingImage(e, "profile")} />
                  <div className="upload-zone-icon"></div>
                  <div className="fs-13 fw-600">Profile Picture</div>
                  <div className="text-muted fs-11">{profileImg ? `✓ ${profileImg.name}` : "Click to upload and crop"}</div>
                  {profileImg && (
                    <button type="button" className="btn btn-ghost btn-xs mt-8" onClick={(event) => {
                      event.stopPropagation();
                      setCropRequest({ file: profileImg, type: "profile" });
                    }}>Adjust crop</button>
                  )}
                </div>
                <div className="upload-zone" data-testid="wiz-cover-zone" onClick={() => coverRef.current?.click()} style={{ padding: "var(--space-4)" }}>
                  <input ref={coverRef} type="file" accept="image/*" onChange={(e) => chooseBrandingImage(e, "cover")} />
                  <div className="upload-zone-icon"></div>
                  <div className="fs-13 fw-600">Cover Banner</div>
                  <div className="text-muted fs-11">{coverImg ? `✓ ${coverImg.name}` : "Click to upload and crop"}</div>
                  {coverImg && (
                    <button type="button" className="btn btn-ghost btn-xs mt-8" onClick={(event) => {
                      event.stopPropagation();
                      setCropRequest({ file: coverImg, type: "cover" });
                    }}>Adjust crop</button>
                  )}
                </div>
              </div>
              <div className="field">
                <div className="field-label">Bio</div>
                <textarea className="field-input" rows={4} value={branding.bio} onChange={(e) => setBranding({ ...branding, bio: e.target.value })} placeholder="Tell event planners what makes you special…" data-testid="wiz-bio" />
              </div>
              <div className="field-row">
                <div className="field">
                  <div className="field-label">Which languages do you speak?</div>
                  <div className="q-multi" data-testid="wiz-languages">
                    {ARTIST_LANGUAGE_OPTIONS.map((language) => (
                      <button
                        key={language}
                        type="button"
                        className={`mult-chip ${branding.languages.includes(language) ? "active" : ""}`}
                        onClick={() => toggleLanguage(language)}
                        data-testid={`wiz-language-${language.toLowerCase()}`}
                      >{language}</button>
                    ))}
                  </div>
                  {branding.languages.includes("Other") && (
                    <input
                      className="field-input mt-8"
                      value={branding.languages_other}
                      onChange={(e) => setBranding({ ...branding, languages_other: e.target.value })}
                      placeholder="Enter another language"
                      data-testid="wiz-language-other"
                    />
                  )}
                </div>
                <div className="field">
                  <div className="field-label">Years of Experience</div>
                  <input type="number" min="0" className="field-input" value={branding.experience_years} onChange={(e) => setBranding({ ...branding, experience_years: e.target.value })} data-testid="wiz-exp" />
                </div>
              </div>
              <div className="flex gap-12">
                <button className="btn btn-ghost" onClick={() => setStep(1)} data-testid="wiz-back-2">← Back</button>
                <button className="btn btn-gold" style={{ flex: 1 }} onClick={saveStep2} disabled={busy} data-testid="wiz-next-2">
                  {busy ? "Saving…" : "Continue →"}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="wiz-step-3">
              <div className="font-serif fs-20 fw-700 mb-8">Media Portfolio</div>
              <div className="text-muted fs-13 mb-20">Upload performance photos, videos, and clips. Photos up to 40 MB, videos up to 100 MB.</div>
              {existingGallery.length > 0 && (
                <div className="mb-16" data-testid="wiz-existing-gallery">
                  <div className="fw-600 fs-13 mb-8">Current portfolio uploads ({existingGallery.length})</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 8 }}>
                    {existingGallery.map((media) => (
                      <div key={media.id} className="card" style={{ padding: 8, minWidth: 0, display: "grid", gridTemplateColumns: "48px minmax(0, 1fr) auto", alignItems: "center", gap: 8 }}>
                        {media.mime?.startsWith("image/") ? (
                          <img
                            src={mediaUrl(media.id)}
                            alt={media.title || "Gallery image"}
                            style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                          />
                        ) : (
                          <div aria-hidden="true" style={{ width: 48, height: 48, display: "grid", placeItems: "center", borderRadius: 6, background: "var(--bg3)" }}>
                            {media.mime?.startsWith("video/") ? "Video" : "File"}
                          </div>
                        )}
                        <span className="fs-12" style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {media.title || "Gallery image"}
                        </span>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() => removeExistingGalleryItem(media)}
                          disabled={Boolean(deletingMediaId)}
                          data-testid={`wiz-media-remove-${media.id}`}
                        >
                          {deletingMediaId === media.id ? "Removing…" : "Remove"}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="upload-zone mb-16" data-testid="wiz-gallery-zone" onClick={() => galleryRef.current?.click()} style={{ padding: "var(--space-8)" }}>
                <input ref={galleryRef} type="file" multiple accept="image/*,video/*" onChange={chooseGalleryFiles} />
                <div className="upload-zone-icon"></div>
                <div className="fs-14 fw-600 mb-4">Drop files here or click to browse</div>
                <div className="text-muted fs-12">{galleryFiles.length ? `${galleryFiles.length} file(s) selected` : "Bulk upload supported · Photos up to 40 MB · Videos up to 100 MB"}</div>
              </div>
              {galleryFiles.some((file) => file.type.startsWith("image/")) && (
                <div className="mb-16" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: 8 }}>
                  {galleryFiles.map((file, index) => file.type.startsWith("image/") && (
                    <button
                      key={`${file.name}-${file.size}-${index}`}
                      type="button"
                      className="btn btn-ghost btn-xs"
                      style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      onClick={() => setCropRequest({ file, type: "gallery", index })}
                      data-testid={`wiz-gallery-crop-${index}`}
                    >Edit {file.name}</button>
                  ))}
                </div>
              )}
              <div className="card card-pad mb-16" data-testid="wiz-tech-rider">
                <div className="fw-600 fs-13 mb-4">Technical rider <span className="text-muted fw-400">(optional)</span></div>
                <div className="text-muted fs-12 mb-8">
                  Have a stage plot, sound input list, or equipment requirements PDF? Upload it for event organisers. If you don't have one, skip this—many artists don't need a formal rider. You can add one later from your dashboard or enter technical details in your profile questionnaire.
                </div>
                {techRider && !techRiderFile && (
                  <div className="text-muted fs-12 mb-8" data-testid="wiz-tech-rider-current">
                    Current file: {techRider.filename}
                  </div>
                )}
                {techRiderFile && (
                  <div className="text-muted fs-12 mb-8" data-testid="wiz-tech-rider-selected">
                    Selected: {techRiderFile.name}
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => setTechRiderFile(null)}>Remove</button>
                  </div>
                )}
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(event) => setTechRiderFile(event.target.files?.[0] || null)}
                  data-testid="wiz-tech-rider-file"
                />
                <div className="text-muted fs-11 mt-4">PDF only · up to 10 MB · optional</div>
              </div>
              <div className="flex gap-12">
                <button className="btn btn-ghost" onClick={() => setStep(2)} data-testid="wiz-back-3">← Back</button>
                <button className="btn btn-gold" style={{ flex: 1 }} onClick={saveStep3} disabled={busy} data-testid="wiz-next-3">
                  {busy ? "Uploading…" : "Continue →"}
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="wiz-step-4">
              <div className="font-serif fs-20 fw-700 mb-8">Your Starting Package</div>
              <div className="text-muted fs-13 mb-20">Create your most popular offering. You can add more packages later.</div>
              <div className="field">
                <div className="field-label">Package Name</div>
                <input className="field-input" value={pkg.name} onChange={(e) => setPkg({ ...pkg, name: e.target.value })} data-testid="wiz-pkg-name" />
              </div>
              <div className="field-row">
                <div className="field">
                  <div className="field-label">Price (₹)</div>
                  <input type="number" min="0" className="field-input" value={pkg.price} onChange={(e) => setPkg({ ...pkg, price: e.target.value })} data-testid="wiz-pkg-price" />
                </div>
                <div className="field">
                  <div className="field-label">Duration (hours)</div>
                  <input type="number" min="0.5" step="0.5" className="field-input" value={pkg.duration} onChange={(e) => setPkg({ ...pkg, duration: e.target.value })} data-testid="wiz-pkg-duration" />
                </div>
              </div>
              <div className="field">
                <div className="field-label">What's Included (one per line)</div>
                <textarea className="field-input" rows={4} value={pkg.features.join("\n")} onChange={(e) => setPkg({ ...pkg, features: e.target.value.split("\n") })} placeholder="Add your package inclusions, one per line" data-testid="wiz-pkg-features" />
              </div>
              <div className="flex gap-12">
                <button className="btn btn-ghost" onClick={() => setStep(3)} data-testid="wiz-back-4">← Back</button>
                <button className="btn btn-gold" style={{ flex: 1 }} onClick={saveStep4} disabled={busy} data-testid="wiz-next-4">
                  {busy ? "Saving…" : "Continue →"}
                </button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="wiz-step-5">
              <div className="font-serif fs-20 fw-700 mb-8">Availability <span className="text-muted">(Optional)</span></div>
              <div className="text-muted fs-13 mb-20">Add an available date now, or skip this step and manage your calendar later. Availability does not block profile setup.</div>
              <div className="field">
                <div className="field-label">Next Available Date</div>
                <input type="date" className="field-input" value={availDate} onChange={(e) => setAvailDate(e.target.value)} data-testid="wiz-avail-date" />
                <div className="flex gap-8 mt-8">
                  <button type="button" className={`btn btn-xs ${availDate ? "btn-ghost" : "btn-gold"}`} onClick={() => selectAvailabilityDate(0)} data-testid="wiz-avail-today">Today</button>
                  <button type="button" className="btn btn-ghost btn-xs" onClick={() => selectAvailabilityDate(1)} data-testid="wiz-avail-tomorrow">Tomorrow</button>
                </div>
                <div className="field-hint">Tip: mark next weekend so you're discoverable for upcoming events.</div>
              </div>
              <div className="card card-pad mb-16" style={{ background: "rgba(212,175,55,0.08)" }}>
                <div className="fs-13 fw-700 text-gold mb-8"> You're almost done!</div>
                <div className="text-muted fs-12">
                  This completes your profile setup. Submit KYC next; once it is approved, your profile goes live automatically and your agreement is saved in My Agreements.
                </div>
                <div className="fs-12 mt-8" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                  <span className="text-muted">Review:</span>
                  {[
                    ["Artist Agreement", "/page/artist-agreement"],
                    ["Terms & Conditions", "/page/terms"],
                    ["Privacy Policy", "/page/privacy"],
                  ].map(([label, href]) => (
                    <a
                      key={href}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "6px 10px",
                        borderRadius: 999,
                        border: "1px solid var(--gold-border)",
                        background: "rgba(212,175,55,0.12)",
                        color: "var(--gold-light)",
                        fontWeight: 700,
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                      }}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              </div>
              <div className="flex gap-12" style={{ flexWrap: "wrap" }}>
                <button className="btn btn-ghost" onClick={() => setStep(4)} data-testid="wiz-back-5">← Back</button>
                <button className="btn btn-ghost" onClick={() => saveStep5(false)} disabled={busy} data-testid="wiz-skip-availability">
                  {busy ? "Saving…" : "Continue without availability"}
                </button>
                <button className="btn btn-gold" onClick={saveStep5} disabled={busy} data-testid="wiz-finish">
                  {busy ? "Saving…" : availDate ? "Save & Finish Setup" : "Finish Setup"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {cropRequest && (
        <ImageCropDialog
          file={cropRequest.file}
          aspectRatio={cropRequest.type === "profile" ? 1 : cropRequest.type === "cover" ? 10 / 3 : undefined}
          title={cropRequest.type === "profile" ? "Crop profile picture" : cropRequest.type === "cover" ? "Crop cover banner" : "Edit portfolio image"}
          onCancel={() => setCropRequest(null)}
          onSave={finishBrandingCrop}
        />
      )}
    </div>
  );
}
